const ACCESS_MODE_KEYS = ["subscription", "buy", "rent"];

// Stable-enough React keys for Category/Sub Category rows (add/remove only,
// never reordered), without pulling in a uuid dependency.
let placementKeySeq = 0;
export const nextPlacementKey = () => `placement-${Date.now()}-${placementKeySeq++}`;

// Normalizes a stored placement id (Prisma Int) to the string a <select>
// value compares against -- without this an edit's saved rows render as
// "Select" because value={5} never matches <option value="5">.
export const toPlacementId = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

// A Category/Sub Category row is only usable once BOTH halves are picked.
export const isCompletePlacement = (placement) =>
  Boolean(placement?.section_id) && Boolean(placement?.category_id);

export const placementPairKey = (placement) =>
  `${toPlacementId(placement?.section_id)}:${toPlacementId(placement?.category_id)}`;

// Whether the Publishing form's Category / Sub Category section is shown for a
// given route hash. The episode and season forms carry the placement on their
// parent Series, so they hide it entirely.
//
// Single source of truth for BOTH the render gate (<Publishing>) and the
// save-time validation gate in the parent forms: validating on a different
// rule than the one that renders the field would block a save over something
// the user was never shown.
export const showsPlacementSection = (hash = "") => {
  const isEpisodeForm = hash.split("/").length >= 5 && hash.includes("/video/");
  return !isEpisodeForm && !hash.includes("/episodes") && !hash.includes("/seasons/add");
};

export const parseAccessTypes = (accessType) => {
  if (!accessType || typeof accessType !== "string") return [];
  return accessType
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((s) => ACCESS_MODE_KEYS.includes(s));
};

export const appendPublishingPayload = (payload, publishingData, statusOverride) => {
  // Access is only Free / Paid: no access_type or buy/rent pricing is sent.
  payload.append("status", publishingData.status || statusOverride || "");
  payload.append("access", publishingData.access || "");

  payload.append("publishing_date", publishingData.publishing_date || "");
  payload.append("ppv_price", publishingData.ppvPrice || "");
  payload.append("license_expiry", publishingData.licenseExpiry || "");
  payload.append("featured_placement", publishingData.featuredPlacement || "");
  payload.append("featured_until", publishingData.featuredUntil || "");

  // Category / Sub Category rows (Experience Builder placement requests) --
  // stored on the content record as a request; only applied to the actual
  // page/rail when an Admin approves the submission (see
  // VideoController.submissionStatusChange). FormData has no array type, so
  // this goes over as a JSON string of [{section_id, category_id}, ...].
  const placements = (publishingData.category_placements || [])
    .filter((p) => p.section_id && p.category_id)
    .map((p) => ({ section_id: parseInt(p.section_id, 10), category_id: parseInt(p.category_id, 10) }));
  payload.append("category_placements", JSON.stringify(placements));
};

export const mapPublishingFromApi = (data, prev, { trustStatus = true } = {}) => {
  const accessType = data.access_type ?? prev.access_type;
  const types = parseAccessTypes(accessType);

  return {
    ...prev,
    status: trustStatus ? (data.status ?? prev.status) : prev.status,
    access: data.access ?? prev.access,
    access_type: accessType,
    access_types: types.length ? types : prev.access_types ?? [],
    rent_price: data.rent_price ?? prev.rent_price,
    rent_duration_days: data.rent_duration_days ?? prev.rent_duration_days,
    buy_price: data.buy_price ?? prev.buy_price,
    currency: data.currency ?? prev.currency,
    category_placements: Array.isArray(data.requested_placements)
      ? data.requested_placements.map((p) => ({
          _key: nextPlacementKey(),
          section_id: toPlacementId(p.section_id),
          category_id: toPlacementId(p.category_id),
        }))
      : prev.category_placements ?? [],
  };
};

// Shared rule for the Publishing form's Category/Sub Category rows. Callers
// must gate on showsPlacementSection() first: the episode and season forms
// hide the field, so validating there would block a save over something the
// user was never shown. Required for Save Draft as well as Publish.
// Returns an error string, or null when valid.
// Builds the set of selectable {section, category} pairs from the loaded
// Experience Builder pages.
export const buildValidPairs = (sections) => {
  const validPairs = new Set();
  (Array.isArray(sections) ? sections : []).forEach((section) => {
    (section?.categories || []).forEach((category) => {
      validPairs.add(placementPairKey({ section_id: section.id, category_id: category.id }));
    });
  });
  return validPairs;
};

// `sections` is optional: when the caller passes the loaded Experience Builder
// pages, rows pointing at a page that genuinely no longer exists are rejected
// rather than silently re-saved as a stale id.
//
// This only runs when the caller confirms the page list is COMPLETE. A
// truncated list (a tenant with more pages than one request returns) would
// otherwise make every placement outside the first page look deleted, blocking
// edits on content that is perfectly valid -- with no way to fix it from the
// form. Unknown-completeness therefore fails open.
export const validateCategoryPlacements = (publishingData, sections, { sectionsComplete = true } = {}) => {
  const rows = Array.isArray(publishingData?.category_placements)
    ? publishingData.category_placements
    : [];

  if (sectionsComplete && Array.isArray(sections) && sections.length) {
    const validPairs = buildValidPairs(sections);
    const stale = rows.some(
      (p) => isCompletePlacement(p) && !validPairs.has(placementPairKey(p))
    );
    if (stale) {
      return "A selected Category is no longer available. Please pick another one.";
    }
  }

  // Placements are OPTIONAL: content saves fine with no Category at all.
  // What is not allowed is a HALF-filled row -- a Category with no Sub
  // Category is dropped silently on save (appendPublishingPayload filters it
  // out), so the user would believe they placed the content somewhere and
  // nothing would happen. An empty row is ignored; a started one must finish.
  if (rows.some((p) => p?.section_id && !p?.category_id)) {
    return "Please select a Sub Category for every Category you added.";
  }

  const complete = rows.filter(isCompletePlacement);

  const seen = new Set();
  for (const placement of complete) {
    const key = placementPairKey(placement);
    if (seen.has(key)) {
      return "The same Category and Sub Category is selected more than once.";
    }
    seen.add(key);
  }

  return null;
};
